import React, { useMemo, useState } from "react";
import { Autocomplete as MuiAutocomplete, TextField, Grid, Typography, Chip, Checkbox } from "@mzc-pdc/ui";
import { IconErrorCircle, IconCancelCircle, IconWarningTriangle } from "@mzc-cloudplex/icons";
import { useTranslation } from "react-i18next";

export const Autocomplete = ({
    id,
    multiple = true,
    value,
    options,
    onClickOption,
    placeholder,
    error,
    helperText,
    open: externalOpen, // 외부에서 제어하는 경우
    onOpen: externalOnOpen,
    onClose: externalOnClose,
    ...rest
}) => {
    const { t } = useTranslation();

    // 내부 상태 (multiple, limitTags 동시 사용시 textfield 펼치면 옵션메뉴가 닫히는 이슈 처리때문에 사용)
    const [internalOpen, setInternalOpen] = useState(false);

    const isControlled = externalOpen !== undefined;
    const open = isControlled ? externalOpen : internalOpen;

    const defaultValue = multiple ? [] : null;
    const getEndAdornment = useMemo(() => {
        if (error) {
            if (!helperText) return <IconWarningTriangle color={"warning"} size={16} />;
            return <IconErrorCircle color={`error`} size={16} />;
        }
        return undefined;
    }, [error, helperText]);

    const handleOpen = (event) => {
        if (externalOnOpen) {
            externalOnOpen(event);
        }
        if (!isControlled) {
            setInternalOpen(true);
        }
    };

    const handleClose = (event, reason) => {
        if (externalOnClose) {
            externalOnClose(event, reason);
        }

        if (!isControlled) {
            // multiple일 때만 pointer 이벤트 무시
            if (multiple) {
                if (reason === "blur" || reason === "escape" || reason === "selectOption") {
                    setInternalOpen(false);
                }
            } else {
                setInternalOpen(false);
            }
        }
    };

    return (
        <MuiAutocomplete
            multiple={multiple}
            disableClearable={multiple}
            disableCloseOnSelect={multiple}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            value={value ?? defaultValue}
            options={options ?? []}
            renderInput={(params) => {
                return (
                    <TextField
                        {...params}
                        placeholder={value ? undefined : placeholder}
                        inputProps={{
                            ...params.inputProps,
                            value: params.inputProps?.value ?? "",
                            endAdornment: getEndAdornment,
                        }}
                    />
                );
            }}
            renderOption={(props, option) => (
                <li {...props}>
                    <>
                        {multiple && (
                            <Checkbox
                                color={"greySecondary"}
                                style={{ marginRight: 8 }}
                                checked={value?.find((x) => x === option.value) !== undefined}
                            />
                        )}
                        <>{option.label}</>
                    </>
                </li>
            )}
            isOptionEqualToValue={(option, value) => option.value === value}
            renderTags={(value, getTagProps) => {
                return value.map((option, index) => (
                    <Chip
                        size={"small"}
                        label={t(`common::label::${option}`, option)}
                        {...getTagProps({
                            index,
                        })}
                        key={index}
                    />
                ));
            }}
            onChange={(e, newValue) => {
                if (multiple) {
                    if (newValue?.length === 0) newValue = undefined;
                    onClickOption(
                        id,
                        newValue?.reduce((acc, cur) => (!cur.value ? [...acc, cur] : [...acc, cur.value]), []),
                    );
                } else {
                    onClickOption(id, newValue?.value);
                }
            }}
            {...rest}
        />
    );
};
